// Admin · platform (A07, A08, A10–A16, A18, A19): Zod girdileri ve yanıt tipleri.
// Gizlilik: yolcu adı/telefonu bu sözleşmelerde YOK; panel kullanıcısı telefonu maskeli, şoför yalnız baş harf,
// plaka canlı operasyonda maskeli ("34 ••• 145").
import { z } from 'zod';
import type { Direction, TenantType } from '../enums';
import type { TripDisplay } from '../state';
import type { HealthCheck, HealthLevel, SystemHealthSummary } from './admin';
import { IsoDate, type Paged } from './common';

const Page = {
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
};

// ───────── ortak yardımcılar ─────────

/** "34 SRT 145" → "34 ••• 145" (canlı operasyon ve GPS sağlığında plaka maskeli). */
export function maskPlate(plate: string): string {
  const parts = plate.trim().split(/\s+/);
  if (parts.length >= 3) return `${parts[0]} ••• ${parts[parts.length - 1]}`;
  const s = plate.replace(/\s+/g, '');
  return s.length > 5 ? `${s.slice(0, 2)} ••• ${s.slice(-3)}` : '•••';
}

/** Sürüm biçimi: 1.4.0 (major.minor.patch). */
export const SEMVER_RE = /^\d{1,3}\.\d{1,3}\.\d{1,4}$/;

export function compareSemver(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) if ((pa[i] ?? 0) !== (pb[i] ?? 0)) return (pa[i] ?? 0) - (pb[i] ?? 0);
  return 0;
}

/** Şablondaki `{değişken}` adları (sırayla, tekil). Türkçe karakter ve boşluk içerebilir: `{duyuru metni}`. */
export function templateVariables(text: string): string[] {
  return [...new Set([...text.matchAll(/\{([^{}]+)\}/g)].map((m) => m[1]!.trim()))];
}

/** Değişkenleri örnek/gerçek değerlerle doldurur; bilinmeyen değişken olduğu gibi kalır. */
export function fillTemplate(text: string, vars: Record<string, string | number | undefined>): string {
  return text.replace(/\{([^{}]+)\}/g, (all, k: string) => {
    const v = vars[k.trim()];
    return v === undefined ? all : String(v);
  });
}

/**
 * Şablon değişken doğrulaması: özgün şablondaki her `{değişken}` başlıkta veya metinde kalmalı; özgünde olmayan
 * değişken eklenemez (gönderimde doldurulamaz).
 */
export function checkTemplateVariables(
  allowed: string[],
  title: string,
  body: string,
): { missing: string[]; unknown: string[] } {
  const used = templateVariables(`${title}\n${body}`);
  return {
    missing: allowed.filter((v) => !used.includes(v)),
    unknown: used.filter((v) => !allowed.includes(v)),
  };
}

// ───────── A07 Platform canlı operasyon ─────────

export interface PlatformLiveVehicle {
  tripId: string;
  companyId: string;
  companyName: string;
  institutionName: string;
  /** "34 ••• 145" */
  plateMasked: string;
  routeCode: string;
  direction: Direction;
  display: TripDisplay;
  delayMin: number;
  gpsLost: boolean;
  lastPingAt: string | null;
  position: { lat: number; lng: number; heading: number } | null;
}

export interface PlatformLiveCompany {
  companyId: string;
  companyName: string;
  activeTrips: number;
  delayed: number;
  critical: number;
  criticalAlerts: number;
  gpsLost: number;
  onMap: number;
}

export interface PlatformLive {
  at: string;
  date: string;
  totals: {
    companies: number;
    activeTrips: number;
    delayed: number;
    critical: number;
    criticalAlerts: number;
    gpsLost: number;
  };
  vehicles: PlatformLiveVehicle[];
  companies: PlatformLiveCompany[];
}

// ───────── A08 GPS sağlığı ─────────

export const GPS_STATES = ['LIVE', 'LAGGING', 'LOST', 'IDLE'] as const;
export type GpsState = (typeof GPS_STATES)[number];
export const GPS_STATE_LABELS: Record<GpsState, string> = {
  LIVE: 'Canlı',
  LAGGING: 'Gecikmeli',
  LOST: 'Sinyal yok',
  IDLE: 'Sefer dışı',
};
export const GPS_STATE_HINTS: Record<GpsState, string> = {
  LIVE: 'Son 60 sn içinde konum geldi',
  LAGGING: 'Konum geliyor ama gecikmeli',
  LOST: 'Etkin seferde konum, firmanın GPS kaybı eşiğini aştı',
  IDLE: 'Etkin sefer yok; son konum bilgi amaçlı',
};

export const GpsHealthQuery = z.object({
  companyId: z.string().max(64).optional(),
  state: z.enum(GPS_STATES).optional(),
});
export type GpsHealthQuery = z.infer<typeof GpsHealthQuery>;

export interface GpsHealthRow {
  vehicleId: string;
  companyId: string;
  companyName: string;
  plateMasked: string;
  /** şoför yalnız baş harf (A08) */
  driverInitials: string | null;
  routeCode: string | null;
  activeTrip: boolean;
  state: GpsState;
  lastPingAt: string | null;
  lastPingAgeS: number | null;
  battery: number | null;
  accuracyM: number | null;
  /** son 15 dk ortalama dakikadaki ping */
  pingsPerMin: number;
  deviceId: string | null;
  /** firmanın GPS kaybı eşiği (dk) */
  gpsLostMin: number;
}

export interface GpsBucket {
  label: string;
  count: number;
}

export interface GpsHealth {
  at: string;
  rows: GpsHealthRow[];
  counts: Record<GpsState, number>;
  accuracy: GpsBucket[];
  battery: GpsBucket[];
  pingAge: GpsBucket[];
  ingest: { pingsLast5Min: number; pingsLast15Min: number; vehiclesLast15Min: number };
  companies: { id: string; name: string }[];
}

// ───────── A10–A11 Destek ─────────

export const TICKET_STATUSES = ['OPEN', 'PENDING', 'RESOLVED'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];
export const TICKET_STATUS_LABELS: Record<TicketStatus, string> = {
  OPEN: 'Açık',
  PENDING: 'Beklemede',
  RESOLVED: 'Çözüldü',
};

export const TICKET_PRIORITIES = ['LOW', 'NORMAL', 'HIGH', 'URGENT'] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];
export const TICKET_PRIORITY_LABELS: Record<TicketPriority, string> = {
  LOW: 'Düşük',
  NORMAL: 'Normal',
  HIGH: 'Yüksek',
  URGENT: 'Acil',
};
/** İlk yanıt hedefi (saat) — önceliğe göre. */
export const TICKET_SLA_HOURS: Record<TicketPriority, number> = { URGENT: 2, HIGH: 8, NORMAL: 24, LOW: 72 };

export const TICKET_APPS = ['console', 'driver', 'passenger'] as const;
export type TicketApp = (typeof TICKET_APPS)[number];
export const TICKET_APP_LABELS: Record<TicketApp, string> = {
  console: 'Panel',
  driver: 'Şoför uygulaması',
  passenger: 'Yolcu uygulaması',
};

/** met: ilk yanıt süresinde verildi · running: süre işliyor · breached: aşıldı */
export type TicketSla = 'met' | 'running' | 'breached';
export const TICKET_SLA_LABELS: Record<TicketSla, string> = {
  met: 'Süresinde yanıtlandı',
  running: 'Süre işliyor',
  breached: 'Süre aşıldı',
};

export const AdminTicketQuery = z.object({
  status: z.enum(['active', 'all', ...TICKET_STATUSES]).default('active'),
  priority: z.enum(TICKET_PRIORITIES).optional(),
  app: z.enum(TICKET_APPS).optional(),
  companyId: z.string().max(64).optional(),
  /** me | none | kullanıcı kimliği */
  assignee: z.string().max(64).optional(),
  q: z.string().trim().max(80).optional(),
  ...Page,
});
export type AdminTicketQuery = z.infer<typeof AdminTicketQuery>;

export interface AdminTicketRow {
  id: string;
  number: string;
  subject: string;
  company: { id: string; name: string } | null;
  app: TicketApp;
  /** talep sahibi: yalnız baş harf + rol (telefon yok) */
  requester: { initials: string; role: string };
  status: TicketStatus;
  priority: TicketPriority;
  assignee: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
  messageCount: number;
  firstResponseAt: string | null;
  slaDueAt: string;
  sla: TicketSla;
}

export interface AdminTicketPage extends Paged<AdminTicketRow> {
  counts: { open: number; pending: number; resolved: number; unassigned: number; breached: number };
}

export interface AdminTicketMessage {
  id: string;
  body: string;
  /** iç not: talep sahibi asla görmez */
  internal: boolean;
  author: { kind: 'requester' | 'staff' | 'other'; name: string };
  at: string;
}

export interface AdminTicketAssignee {
  id: string;
  name: string;
  roleName: string;
}

export interface AdminTicketDetail extends AdminTicketRow {
  messages: AdminTicketMessage[];
  assignees: AdminTicketAssignee[];
}

export const AdminTicketReplyInput = z.object({
  body: z.string().trim().min(2, 'Mesajı yazın').max(4000),
  internal: z.boolean().default(false),
  /** yanıtla birlikte çözüldü olarak kapat */
  resolve: z.boolean().default(false),
});
export type AdminTicketReplyInput = z.infer<typeof AdminTicketReplyInput>;

export const AdminTicketUpdateInput = z
  .object({
    status: z.enum(TICKET_STATUSES).optional(),
    priority: z.enum(TICKET_PRIORITIES).optional(),
    assigneeUserId: z.string().max(64).nullable().optional(),
  })
  .refine((v) => v.status !== undefined || v.priority !== undefined || v.assigneeUserId !== undefined, {
    message: 'Değiştirilecek bir alan seçin',
  });
export type AdminTicketUpdateInput = z.infer<typeof AdminTicketUpdateInput>;

// ───────── A12 Güvenli kimliğe bürünme ─────────

export const IMPERSONATION_DURATIONS = [15, 30, 60] as const;
export type ImpersonationDuration = (typeof IMPERSONATION_DURATIONS)[number];

export const ImpersonationStartInput = z.object({
  companyId: z.string().min(1).max(64),
  userId: z.string().min(1).max(64),
  reason: z.string().trim().min(15, 'Gerekçe en az 15 karakter olmalı').max(300),
  durationMin: z.union([z.literal(15), z.literal(30), z.literal(60)]),
});
export type ImpersonationStartInput = z.infer<typeof ImpersonationStartInput>;

export const ImpersonationExchangeInput = z.object({ code: z.string().min(16).max(128) });

export const ImpersonationQuery = z.object({
  state: z.enum(['active', 'all']).default('all'),
  ...Page,
});
export type ImpersonationQuery = z.infer<typeof ImpersonationQuery>;

export interface ImpersonationCompanyOption {
  id: string;
  name: string;
  type: TenantType;
  status: string;
}

export interface ImpersonationUserOption {
  userId: string;
  name: string;
  roleName: string;
  phoneMasked: string;
}

export type ImpersonationState = 'ACTIVE' | 'ENDED' | 'EXPIRED';
export const IMPERSONATION_STATE_LABELS: Record<ImpersonationState, string> = {
  ACTIVE: 'Etkin',
  ENDED: 'Sonlandırıldı',
  EXPIRED: 'Süresi doldu',
};

export interface ImpersonationRow {
  id: string;
  admin: { id: string; name: string };
  company: { id: string; name: string; type: TenantType } | null;
  target: { id: string; name: string; roleName: string | null; phoneMasked: string } | null;
  reason: string;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  state: ImpersonationState;
  /** oturum boyunca hedef kullanıcı adına denetime düşen işlem sayısı */
  actions: number;
  mine: boolean;
}

export interface ImpersonationList extends Paged<ImpersonationRow> {
  active: number;
}

export interface ImpersonationStartResult {
  id: string;
  /** tek kullanımlık kodlu konsol adresi (60 sn) */
  url: string;
  expiresAt: string;
}

export interface ImpersonationExchangeResult {
  accessToken: string;
  refreshToken: string;
  companyId: string;
  tenantType: TenantType;
  expiresAt: string;
}

// ───────── A13 Güvenlik merkezi ─────────

export const SECURITY_EVENT_TYPES = [
  'FAILED_OTP',
  'OTP_RATE_LIMIT',
  'SUSPICIOUS_LOGIN',
  'REFRESH_REUSE',
  'ABUSE',
] as const;
export type SecurityEventType = (typeof SECURITY_EVENT_TYPES)[number];
export const SECURITY_EVENT_LABELS: Record<SecurityEventType, string> = {
  FAILED_OTP: 'Hatalı doğrulama kodu',
  OTP_RATE_LIMIT: 'Kod isteği sınırı',
  SUSPICIOUS_LOGIN: 'Şüpheli giriş',
  REFRESH_REUSE: 'Oturum belirteci yeniden kullanıldı',
  ABUSE: 'Kötüye kullanım',
};

export const SECURITY_SEVERITIES = ['INFO', 'WARNING', 'CRITICAL'] as const;
export type SecuritySeverity = (typeof SECURITY_SEVERITIES)[number];
export const SECURITY_SEVERITY_LABELS: Record<SecuritySeverity, string> = {
  INFO: 'Bilgi',
  WARNING: 'Uyarı',
  CRITICAL: 'Kritik',
};

export const SecurityEventQuery = z.object({
  type: z.enum(SECURITY_EVENT_TYPES).optional(),
  severity: z.enum(SECURITY_SEVERITIES).optional(),
  reviewed: z.enum(['all', 'open', 'reviewed']).default('all'),
  ...Page,
});
export type SecurityEventQuery = z.infer<typeof SecurityEventQuery>;

export interface SecuritySubject {
  userId: string;
  /** baş harfler */
  initials: string;
  status: string;
  /** SEROTA personeli: askıya alınamaz */
  platform: boolean;
  companies: string[];
}

export interface SecurityEventRow {
  id: string;
  type: SecurityEventType | string;
  severity: SecuritySeverity;
  at: string;
  ip: string | null;
  phoneMasked: string | null;
  app: string | null;
  subject: SecuritySubject | null;
  reviewed: { by: string; at: string } | null;
}

export interface SecurityOverview {
  kpis: {
    failedOtp24h: number;
    rateLimit24h: number;
    refreshReuse24h: number;
    suspicious24h: number;
    critical7d: number;
    open: number;
    suspendedUsers: number;
  };
  trend: { labels: string[]; series: { type: SecurityEventType; values: number[] }[] };
  topIps: { ip: string; count: number }[];
  topPhones: { phoneMasked: string; count: number }[];
}

export const SuspendUserInput = z.object({
  reason: z.string().trim().min(10, 'Gerekçe en az 10 karakter olmalı').max(300),
});

// ───────── A14 KVKK ─────────

export const PLATFORM_KVKK_TYPES = ['EXPORT', 'DELETE', 'ANONYMIZE'] as const;
export type PlatformKvkkType = (typeof PLATFORM_KVKK_TYPES)[number];
export const PLATFORM_KVKK_TYPE_LABELS: Record<PlatformKvkkType, string> = {
  EXPORT: 'Dışa aktarma',
  DELETE: 'Silme',
  ANONYMIZE: 'Anonimleştirme',
};

export const PLATFORM_KVKK_STATUSES = ['PENDING', 'PROCESSING', 'DONE', 'REJECTED'] as const;
export type PlatformKvkkStatus = (typeof PLATFORM_KVKK_STATUSES)[number];
export const PLATFORM_KVKK_STATUS_LABELS: Record<PlatformKvkkStatus, string> = {
  PENDING: 'Bekliyor',
  PROCESSING: 'İşleniyor',
  DONE: 'Tamamlandı',
  REJECTED: 'Reddedildi',
};

/** KVKK md. 13: başvuru en geç 30 gün içinde sonuçlandırılır. */
export const PLATFORM_KVKK_LEGAL_DAYS = 30;

export type PlatformKvkkDeadline = 'ok' | 'soon' | 'overdue' | 'closed';

export const PlatformKvkkQuery = z.object({
  status: z.enum(['open', 'all', ...PLATFORM_KVKK_STATUSES]).default('open'),
  type: z.enum(PLATFORM_KVKK_TYPES).optional(),
  companyId: z.string().max(64).optional(),
  ...Page,
});
export type PlatformKvkkQuery = z.infer<typeof PlatformKvkkQuery>;

export interface PlatformKvkkRow {
  id: string;
  type: PlatformKvkkType;
  status: PlatformKvkkStatus;
  company: { id: string; name: string } | null;
  /** maskeli ilgili kişi: "Kullanıcı · E. U. · +90 5•• ••• •• 00" */
  subject: { kind: 'USER' | 'PASSENGER' | 'COMPANY' | 'UNKNOWN'; label: string };
  requestedBy: string;
  reason: string | null;
  createdAt: string;
  dueAt: string;
  daysLeft: number;
  deadline: PlatformKvkkDeadline;
  completedAt: string | null;
  rejection: string | null;
  /** uygulanabilir mi (kişi belirtilmemiş silme/anonimleştirme uygulanamaz) */
  executable: boolean;
}

export interface PlatformKvkkPage extends Paged<PlatformKvkkRow> {
  counts: { open: number; overdue: number; soon: number; done30d: number };
}

export const PlatformKvkkRejectInput = z.object({
  reason: z.string().trim().min(10, 'Gerekçe en az 10 karakter olmalı').max(500),
});

export interface RetentionRow {
  companyId: string;
  companyName: string;
  gpsRetentionDays: number;
  oldestPingAt: string | null;
  oldestPingAgeDays: number | null;
  pings: number;
  exceeding: boolean;
}

// ───────── A15 Uygulama sürümleri ─────────

export const VERSION_APPS = ['passenger', 'driver'] as const;
export type VersionApp = (typeof VERSION_APPS)[number];
export const VERSION_APP_LABELS: Record<VersionApp, string> = { passenger: 'Yolcu', driver: 'Şoför' };
export const VERSION_PLATFORMS = ['ios', 'android'] as const;
export type VersionPlatform = (typeof VERSION_PLATFORMS)[number];
export const VERSION_PLATFORM_LABELS: Record<VersionPlatform, string> = { ios: 'iOS', android: 'Android' };

const Semver = z.string().trim().regex(SEMVER_RE, 'Sürüm 1.4.0 biçiminde olmalı');

export const AppVersionInput = z
  .object({
    latest: Semver,
    minSupported: Semver,
    forceUpdate: z.boolean(),
    notes: z.string().trim().max(300).nullable().optional(),
  })
  .refine((v) => compareSemver(v.minSupported, v.latest) <= 0, {
    path: ['minSupported'],
    message: 'En düşük desteklenen sürüm, son sürümden büyük olamaz',
  });
export type AppVersionInput = z.infer<typeof AppVersionInput>;

export interface AppVersionRow {
  id: string;
  app: VersionApp;
  platform: VersionPlatform;
  latest: string;
  minSupported: string;
  forceUpdate: boolean;
  notes: string | null;
  updatedAt: string;
  /** bu uygulama × platform için kayıtlı bildirim cihazı (sürüm bilgisi uygulamalardan gelmiyor) */
  devices: number;
}

export interface AppVersionList {
  items: AppVersionRow[];
  /** son 30 günde açılan etkin oturumlar (uygulamaya göre; platform/sürüm ayrımı yok) */
  sessions: { app: VersionApp; active: number }[];
}

// ───────── A16 Bildirim şablonları ─────────

export const TEMPLATE_CHANNELS = ['PUSH', 'EMAIL', 'SMS', 'IN_APP'] as const;
export type TemplateChannel = (typeof TEMPLATE_CHANNELS)[number];
export const TEMPLATE_CHANNEL_LABELS: Record<TemplateChannel, string> = {
  PUSH: 'Anlık bildirim',
  EMAIL: 'E-posta',
  SMS: 'SMS',
  IN_APP: 'Uygulama içi',
};

export const TEMPLATE_KEY_LABELS: Record<string, string> = {
  TRIP_STARTED: 'Sefer başladı',
  APPROACHING: 'Servis yaklaşıyor',
  ARRIVED: 'Servis durakta',
  DELAY: 'Gecikme',
  VEHICLE_CHANGED: 'Araç değişti',
  DRIVER_CHANGED: 'Şoför değişti',
  CHILD_BOARDED: 'Çocuk servise bindi',
  CHILD_ARRIVED: 'Çocuk okula ulaştı',
  CHILD_DELIVERED: 'Çocuk teslim edildi',
  ANNOUNCEMENT: 'Duyuru',
  EMAIL_WELCOME: 'E01 · Hoş geldiniz',
  EMAIL_PASSENGER_INVITE: 'E02 · Yolcu daveti',
  EMAIL_TEAM_INVITE: 'E03 · Ekip daveti',
  EMAIL_REPORT_READY: 'E04 · Rapor hazır',
  EMAIL_PAYMENT: 'E05 · Ödeme',
  EMAIL_DOCUMENT_EXPIRY: 'E06 · Belge süresi',
  EMAIL_COMPLAINT_RESOLVED: 'E07 · Talep çözüldü',
};

/** Canlı önizleme örnek değerleri (seed'deki örnek kişiler; gerçek kişisel veri değil). */
export const TEMPLATE_SAMPLE_VALUES: Record<string, string> = {
  sefer: 'R07 Sabah',
  plaka: '34 SRT 145',
  saat: '07:42',
  dk: '6',
  araç: 'Ford Transit',
  sebep: 'trafikten',
  ad: 'Emrah Usta',
  çocuk: 'Elisa',
  durak: 'Beylikdüzü Meydan',
  okul: 'Nova Koleji',
  kişi: 'Ayşe Kaya',
  kurum: 'Nova Koleji',
  'duyuru metni': 'Yarın servisler 15 dakika erken kalkacak.',
  firma: 'ABC Turizm',
  ay: 'Ekim 2026',
  gün: '5',
  no: 'DST-1042',
};

export const TemplateQuery = z.object({ channel: z.enum(TEMPLATE_CHANNELS).optional() });

export interface NotificationTemplateRow {
  id: string;
  key: string;
  label: string;
  channel: TemplateChannel;
  locale: string;
  title: string;
  body: string;
  variables: string[];
  updatedAt: string;
  /** düzenlendi mi (denetimde en az bir sürüm var) */
  edited: boolean;
}

export interface NotificationTemplateList {
  items: NotificationTemplateRow[];
  /** gönderim tarafı şablon geçersiz kılmayı okuyor mu (false: metinler koddaki katalogdan gelir) */
  overridesApplied: boolean;
}

export const TemplateInput = z.object({
  title: z.string().trim().min(2, 'Başlık en az 2 karakter olmalı').max(160),
  body: z.string().trim().min(1, 'Metni yazın').max(1000),
});
export type TemplateInput = z.infer<typeof TemplateInput>;

export const TemplatePreviewQuery = z.object({
  title: z.string().max(160).optional(),
  body: z.string().max(1000).optional(),
});

export interface TemplateVersion {
  id: string;
  at: string;
  actorName: string;
  before: { title: string; body: string } | null;
  after: { title: string; body: string } | null;
}

export interface TemplatePreview {
  subject: string;
  html: string;
  code: string | null;
}

// ───────── A18 Denetim günlükleri ─────────

export const PlatformAuditQuery = z.object({
  companyId: z.string().max(64).optional(),
  actorId: z.string().max(64).optional(),
  /** işlem öneki: "impersonation", "support.ticket" … */
  action: z.string().trim().max(80).optional(),
  entity: z.string().max(40).optional(),
  from: IsoDate.optional(),
  to: IsoDate.optional(),
  impersonation: z.enum(['true', 'false']).optional(),
  ...Page,
});
export type PlatformAuditQuery = z.infer<typeof PlatformAuditQuery>;

export interface PlatformAuditActor {
  id: string;
  name: string;
  kind: 'platform' | 'panel' | 'app';
}

export interface PlatformAuditRow {
  id: string;
  at: string;
  company: { id: string; name: string } | null;
  actor: PlatformAuditActor | null;
  impersonator: { id: string; name: string } | null;
  action: string;
  entity: string;
  entityId: string | null;
  hasChanges: boolean;
}

export interface PlatformAuditDetail extends PlatformAuditRow {
  before: unknown;
  after: unknown;
  ip: string | null;
}

export interface PlatformAuditFacets {
  companies: { id: string; name: string }[];
  actors: { id: string; name: string }[];
  entities: string[];
  prefixes: string[];
}

export const PLATFORM_AUDIT_ACTION_LABELS: Record<string, string> = {
  'impersonation.start': 'Kimliğe bürünme başlatıldı',
  'impersonation.end': 'Kimliğe bürünme sonlandırıldı',
  'support.ticket.reply': 'Destek talebi yanıtlandı',
  'support.ticket.note': 'Destek talebine iç not eklendi',
  'support.ticket.update': 'Destek talebi güncellendi',
  'security.event.review': 'Güvenlik olayı incelendi',
  'security.user.suspend': 'Kullanıcı askıya alındı',
  'security.user.reactivate': 'Kullanıcı askısı kaldırıldı',
  'kvkk.approve': 'KVKK talebi işleme alındı',
  'kvkk.execute': 'KVKK talebi uygulandı',
  'kvkk.reject': 'KVKK talebi reddedildi',
  'app_version.update': 'Uygulama sürümü güncellendi',
  'notification_template.update': 'Bildirim şablonu güncellendi',
  'admin.audit.export': 'Platform denetim günlüğü dışa aktarıldı',
  // Admin › müşteriler (A02–A06, A09, A17)
  'company.create': 'Firma oluşturuldu (SEROTA)',
  'company.suspend': 'Firma hesabı askıya alındı (SEROTA)',
  'company.reactivate': 'Firma hesabı yeniden etkinleştirildi (SEROTA)',
  'subscription.activate': 'Abonelik etkinleştirildi (SEROTA)',
  'subscription.cancel': 'Abonelik dönem sonunda iptal edildi (SEROTA)',
  'subscription.plan_change': 'Plan değiştirildi (SEROTA)',
  'subscription.trial_extend': 'Deneme süresi uzatıldı (SEROTA)',
  'subscription.upgrade_approve': 'Yükseltme talebi onaylandı (SEROTA)',
  'subscription.upgrade_reject': 'Yükseltme talebi reddedildi (SEROTA)',
  'plan.update': 'Plan güncellendi (SEROTA)',
  'whitelabel.domain_status': 'Özel alan adı durumu güncellendi (SEROTA)',
  'export.costs': 'Kullanım maliyeti dışa aktarıldı',
};

export const PLATFORM_AUDIT_ENTITY_LABELS: Record<string, string> = {
  ImpersonationSession: 'Kimliğe bürünme',
  SecurityEvent: 'Güvenlik olayı',
  AppVersion: 'Uygulama sürümü',
  NotificationTemplate: 'Bildirim şablonu',
  SupportTicket: 'Destek talebi',
  KvkkRequest: 'KVKK talebi',
  Subscription: 'Abonelik',
  Plan: 'Plan',
};

// ───────── A19 Sistem sağlığı ─────────

export interface QueueStat {
  name: string;
  label: string;
  waiting: number;
  active: number;
  failed: number;
  delayed: number;
}

export interface ProviderStat {
  key: string;
  label: string;
  /** adaptör adı: console, inapp, netgsm, expo … */
  name: string;
  mock: boolean;
}

export interface SystemHealthDetail extends SystemHealthSummary {
  api: { uptimeS: number; version: string; node: string; env: string; startedAt: string };
  queues: { enabled: boolean; items: QueueStat[] };
  realtime: { connections: number; subscriptions: number };
  gps: { pingsLast5Min: number; vehiclesLast5Min: number; activeTrips: number };
  /** süreç içi sayaç (bu API örneği, son 15 dk) */
  requests: {
    windowMin: number;
    total: number;
    errors4xx: number;
    errors5xx: number;
    errorRatePct: number | null;
    perMinute: number[];
  };
  providers: ProviderStat[];
  /** hata izleme halkası (API 5xx + istemci raporları); kişisel veri yok, telefonlar maskeli */
  errors: { last15Min: number; recent: RecentErrorItem[] };
}

export const ERROR_SOURCE_LABELS: Record<string, string> = {
  api: 'API',
  console: 'Panel',
  admin: 'Yönetim paneli',
  passenger: 'Yolcu uygulaması',
  driver: 'Şoför uygulaması',
  website: 'Web sitesi',
};

export interface RecentErrorItem {
  source: string;
  message: string;
  route: string | null;
  requestId: string | null;
  at: string;
}

/** Hata sayısı → sağlık seviyesi: 0 çalışıyor, 1–9 yavaş/sorunlu, 10+ çalışmıyor. */
export function errorLevel(count: number): HealthLevel {
  return count >= 10 ? 'down' : count >= 1 ? 'degraded' : 'ok';
}

/** Sağlık seviyesi sırası (en kötü kazanır). */
export function worstLevel(levels: HealthLevel[]): HealthLevel {
  if (levels.includes('down')) return 'down';
  if (levels.includes('degraded')) return 'degraded';
  return 'ok';
}

export type HealthCheckList = HealthCheck[];
